import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToast } from '@bonusly/design-system';
import { useAnalytics, useCurrentUser } from '@bonusly/who-am-i';

import { useEvaluationId } from '../../../hooks/use-evaluation-id';
import { useCreateUnsolicitedFeedbackFromTemplate, useUpdateUnsolicitedFeedbackFromTemplate } from '../../../hooks/use-feedback-api';
import { AmplitudeEvent } from '../../../types/amplitude-event';
import { FeedbackApiData } from '../../../types/feedback';
import { QueryKey } from '../../../types/query-key';

interface SubmitFeedbackTemplate {
	template: {
		[key: string]: string;
	};
	onClose: VoidFunction;
	onReset: VoidFunction;
}

export const useSubmitFeedbackTemplate = ({ template, onReset }: SubmitFeedbackTemplate) => {
	const { t } = useTranslation('feedback');
	const toast = useToast();
	const { track } = useAnalytics();
	const queryClient = useQueryClient();
	const { updateSelectedEvaluationId } = useEvaluationId();
	const { isLoading: isCreating, mutate: createFromTemplate } = useCreateUnsolicitedFeedbackFromTemplate();
	const { isLoading: isUpdating, mutate: updateFromTemplate } = useUpdateUnsolicitedFeedbackFromTemplate();

	const currentUser = useCurrentUser();
	type ResponseWithFeedbackApiData = { data: FeedbackApiData };

	const mutateOptions = {
		onSuccess: (response: ResponseWithFeedbackApiData) => {
			// TODO: instead of using a toast we are going to use a modal and navigate the user to the
			// feedback edit modal with the created feedback using the evaluation_id query string
			toast({
				position: 'bottom-right',
				title: 'Created feedback from your responses',
				status: 'success',
			});
			track(AmplitudeEvent.DraftFromTemplateCreated);
			queryClient.invalidateQueries([QueryKey.UnrequestedFeedbackDrafts]);
			queryClient.invalidateQueries([QueryKey.ConsolidatedFeedbackIndex]);
			queryClient.invalidateQueries(['feedback', response.data.id]);

			onReset();
			updateSelectedEvaluationId(response.data.id);
		},
		onError: () => {
			toast({
				title: t('feedback.unrequested.failure_message'),
				status: 'error',
			});
		},
	};

	const onCreateFeedback = async (recipientId: string) => {
		createFromTemplate(
			{
				giverId: currentUser.id,
				receiverId: recipientId,
				template: template,
			},
			mutateOptions
		);
	};

	const onUpdateFeedback = async (feedbackId: string) => {
		updateFromTemplate(
			{
				feedbackId: feedbackId,
				template: template,
			},
			mutateOptions
		);
	};
	return { onCreateFeedback, onUpdateFeedback, isCreating, isUpdating };
};
